/**
 * Decides what to do when an already-authenticated user lands on a team invite
 * link (/invited-user?type=team&invitation_token=...).
 *
 * Pure function so it can be unit-tested without mounting the navigator or
 * touching sessionStorage. The corresponding effect in app-stack.web.tsx calls
 * this after resolving invitationDetails and dispatches on action.kind.
 */

export type TeamInviteAuthAction =
  /** Valid email invite — accept the mutation directly */
  | { kind: 'accept'; token: string; teamUUId: string }
  /** Link invite or expired email — show public team preview */
  | { kind: 'preview'; teamUUId: string }
  /** Team UUID could not be resolved — clean up and bail */
  | { kind: 'none' };

export const decideTeamInviteAuthAction = ({
  teamUUId,
  autoJoin,
  token,
}: {
  teamUUId: string | null | undefined;
  autoJoin: boolean | null | undefined;
  token: string;
}): TeamInviteAuthAction => {
  if (!teamUUId) return { kind: 'none' };
  if (autoJoin && token) return { kind: 'accept', token, teamUUId };
  return { kind: 'preview', teamUUId };
};

export type TeamInviteNativeDestination =
  /** Team UUID resolved — open the team profile with the invite token */
  | { kind: 'team-details'; teamUUId: string; invitationToken: string }
  /** No UUID — the invite is expired, consumed, or not visible to this user */
  | { kind: 'dead-link' };

/**
 * Native counterpart of decideTeamInviteAuthAction. TeamDetails resolves the
 * team through usePublicTeamFromQuery, which is skipped for an empty
 * teamUUId — navigating there without one renders an empty screen with no
 * name, no members and no way forward. Send the user to the dead-link
 * fallback instead.
 */
export const decideTeamInviteNativeDestination = ({
  teamUUId,
  invitationToken,
}: {
  teamUUId: string | null | undefined;
  invitationToken: string | null | undefined;
}): TeamInviteNativeDestination =>
  teamUUId && invitationToken
    ? { kind: 'team-details', teamUUId, invitationToken }
    : { kind: 'dead-link' };
